"use client";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocale } from "next-intl";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { SwiperSlide } from "swiper/react";
import { useHotelAddToWishlistAnalytics } from "@/components/pages/hotel/search/hooks/useHotelAddToWishlistAnalytics";
import Swiper from "@/components/swiper";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useLoading } from "@/contexts/LoadingContext";
import { useHotelFavorite } from "@/hooks/useHotelFavorite";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { getPathname, Link, useRouter } from "@/i18n/navigation";
import { stripLeadingLocalePrefix } from "@/utils/i18n/stripLeadingLocalePrefix";
import { buildFromLandingData } from "@/utils/hotel/buildFavoritedHotelPayload";
import { getCdnImagePath } from "@/utils/bus/getCdnImagePath";

// Amenity code to icon mapping
const amenityIconMap = {
  3: "circle-wifi",
  7: "pet-custom",
  8: "wheelchair-1",
  9: "circle-parking",
  11: "vault",
  13: "water-ladder-1-1",
  22: "child-hotel",
  23: "air-conditioner",
  27: "bus-service-1",
  28: "umbrella-beach-1",
  30: "hot-tub-person",
  39: "water-ladder-1-1",
  60: "water-ladder-1-1",
  85: "unlock-room",
  88: "cstm-smarttv",
  89: "custom-nespresso",
  107: "hotel-building-new",
  159: "person-swimming-child-1",
  186: "charging-station-1",
  247: "water",
  257: "spa-1",
  267: "shower",
  268: "shower",
  269: "bath",
  270: "hair-dryer",
  272: "mini-bar",
  292: "terrace-new",
  295: "leak-sea",
  296: "leak-sea",
  297: "tree",
  302: "airplane-otel",
  372: "mountbig",
  373: "blueflag",
  404: "tulip",
  563: "umbrella-beach-1",
  720: "circle-parking",
  721: "circle-parking",
  727: "circle-parking",
};

export default function LandingCardItem({
  hotelData,
  onSelect,
  itemListId,
  itemListName,
  passengers,
}) {
  const t = useTranslationDetector("hotel");
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const { show } = useLoading();
  const locale = useLocale();
  const { currency } = useCurrency();

  const { trackAddToWishlist } = useHotelAddToWishlistAnalytics({
    product: hotelData,
    itemListId,
    itemListName,
    passengers,
    currency: currency ?? undefined,
    value: hotelData?.price ?? undefined,
  });

  const favoriteContext = useMemo(
    () => ({ locale: locale ?? "tr", currency: currency ?? "TRY" }),
    [locale, currency],
  );
  const { isFavorite, isAddingFavorite, handleFavoriteClick } =
    useHotelFavorite({
      hotelData,
      getPayload: buildFromLandingData,
      context: favoriteContext,
    });

  // Search sayfasında mıyız kontrol et (URL'de /hotel/search/ veya /otel/ara/ varsa)
  const isSearchPage = useMemo(() => {
    return (
      pathname?.includes("/hotel/search/") || pathname?.includes("/otel/ara/")
    );
  }, [pathname]);

  // Query parametrelerini oluştur
  const queryString = useMemo(() => {
    if (!isSearchPage) {
      // Search sayfası değilse, sadece link'i kullan
      return "";
    }

    const params = new URLSearchParams();

    // check_in ve check_out - pathname'den al
    // URL formatı: /otel/ara/[hotelName]/[checkinDate]/[checkOutDate]
    // veya: /hotel/search/[hotelName]/[checkinDate]/[checkOutDate]
    const pathParts = pathname.split("/").filter(Boolean);
    const searchIndex = pathParts.findIndex(
      (part) => part === "search" || part === "ara",
    );

    // searchIndex + 1 = hotelName, searchIndex + 2 = checkinDate, searchIndex + 3 = checkOutDate
    if (searchIndex !== -1) {
      const checkinDate = pathParts[searchIndex + 2];
      const checkoutDate = pathParts[searchIndex + 3];

      if (checkinDate) {
        params.set("check_in", checkinDate);
      }
      if (checkoutDate) {
        params.set("check_out", checkoutDate);
      }
    }

    // adult ve child - searchParams'tan al
    // URL'de adult ve child var, bunları adult_1 ve child_1'e çeviriyoruz
    const adult =
      searchParams.get("adult") || searchParams.get("adult_1") || "1";
    const child =
      searchParams.get("child") || searchParams.get("child_1") || "0";

    params.set("adult_1", adult);

    if (child && child !== "0") {
      params.set("child_1", child);

      const childCount = parseInt(child, 10);

      // childAges string'ini kontrol et (virgülle ayrılmış: "15,6" veya URL encoded: "15%2C6")
      const childAges = searchParams.get("childAges");
      if (childAges) {
        // URL decode et (15%2C6 -> 15,6)
        const decodedAges = decodeURIComponent(childAges);
        const ages = decodedAges
          .split(",")
          .filter(Boolean)
          .map((age) => age.trim());
        ages.forEach((age, index) => {
          if (index < childCount) {
            params.set(`childage_1_${index + 1}`, age);
          }
        });
      } else {
        // childAges yoksa, childage_1_1, childage_1_2 formatında var mı kontrol et
        for (let i = 1; i <= childCount; i++) {
          const childAge = searchParams.get(`childage_1_${i}`);
          if (childAge) {
            params.set(`childage_1_${i}`, childAge);
          }
        }
      }
    }

    const result = params.toString();

    return result;
  }, [isSearchPage, pathname, searchParams]);

  // Link URL'ini oluştur
  const linkUrl = useMemo(() => {
    const baseLink = stripLeadingLocalePrefix(hotelData.link, locale);
    if (!isSearchPage || !queryString) {
      return baseLink;
    }
    // baseLink zaten query parametreleri içeriyorsa & kullan, yoksa ? kullan
    const separator = baseLink.includes("?") ? "&" : "?";
    const finalUrl = `${baseLink}${separator}${queryString}`;
    if (process.env.NODE_ENV === "development") {
    }

    return finalUrl;
  }, [hotelData.link, isSearchPage, queryString, locale]);

  const triggerSelectAndLoader = () => {
    if (typeof onSelect === "function") {
      onSelect();
    }
    show();
  };

  // "Fiyatları Göster" butonuna tıklandığında loader göster ve sayfaya git
  const handleShowPrices = (e) => {
    e.preventDefault();
    e.stopPropagation();
    triggerSelectAndLoader();
    const localizedPath = getPathname({ href: linkUrl, locale });
    router.push(localizedPath);
  };
  return (
    <Link
      href={linkUrl}
      onClick={triggerSelectAndLoader}
      className="shadow-medium hotel-card-item overflow-hidden rounded-lg bg-white p-4"
    >
      <div className="flex flex-col gap-3 xl:flex-row xl:gap-4">
        {/* Sol taraf - Resim alanı (Swiper carousel için hazırlanmış) */}
        <div className="relative w-full flex-shrink-0 xl:w-[280px]">
          {/* Resim alanı - Swiper carousel buraya eklenecek */}
          <div className="swiper-responsive-container relative h-48 w-full overflow-hidden rounded-lg bg-gray-200 sm:aspect-[280/184]">
            <Swiper
              className="h-full w-full"
              customClass="mobile-hotel-gallery-swiper"
              swiperOptions={{
                loop: true,
                navigation: true,
                navigationPrevButtonCustomClass:
                  "hidden xl:flex absolute top-[50%] text-base! text-foreground-muted  left-4 z-10 m-0 h-[30px]! w-[30px]! -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 p-1 shadow-[0_4px_12px_2px_rgba(20,20,20,0.06)] transition-all duration-300 hover:bg-white",
                navigationNextButtonCustomClass:
                  "hidden xl:flex absolute top-[50%] text-base! text-foreground-muted  right-4 z-10 m-0 h-[30px]! w-[30px]! -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 p-1 shadow-[0_4px_12px_2px_rgba(20,20,20,0.06)] transition-all duration-300 hover:bg-white",
                navigationPrevButtonIcon: (
                  <FontAwesomeIcon
                    className="h-[16px]! w-[16px]!"
                    icon={byPrefixAndName.far["angle-left"]}
                  />
                ),
                navigationNextButtonIcon: (
                  <FontAwesomeIcon
                    className="h-[16px]! w-[16px]!"
                    icon={byPrefixAndName.far["angle-right"]}
                  />
                ),
                pagination: {
                  dynamicBullets: true,
                  dynamicMainBullets: 2,
                  hideOnClick: false,
                  bulletClass: "room-card-bullet",
                  bulletActiveClass: "room-card-bullet-active",
                  containerClass:
                    "absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2",
                },
                mousewheel: true,
                keyboard: true,
                slidesPerView: 1,
                spaceBetween: 0,
              }}
            >
              {hotelData.Images.map((image, index) => (
                <SwiperSlide key={index} className="h-full w-full">
                  <Image
                    src={image || "/images/dummy/hotel-placeholder.jpg"}
                    width={280}
                    height={184}
                    alt={hotelData.name}
                    className="h-full w-full object-cover"
                    unoptimized
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {/* Favori ikonu */}
            <button
              onClick={(e) => {
                if (!isFavorite) trackAddToWishlist();
                handleFavoriteClick(e);
              }}
              disabled={isAddingFavorite}
              className="absolute top-3 right-3 z-1 h-7 w-7 cursor-pointer transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isFavorite ? (
                <Image
                  src={getCdnImagePath("/saka/hotel/like-hotel-fill.svg")}
                  alt="like-card"
                  width={28}
                  height={28}
                />
              ) : (
                <Image
                  src={getCdnImagePath("/saka/hotel/like-hotel.svg")}
                  alt="like-card"
                  width={28}
                  height={28}
                />
              )}
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-[2px] lg:hidden">
          <div className="">
            <h2 className="text-foreground flex flex-wrap items-center gap-x-[6px] text-lg font-semibold">
              {hotelData.name}
              <span className="flex items-center gap-[1px]">
                {[...Array(hotelData.starRating)].map((_, index) => (
                  <FontAwesomeIcon
                    key={index}
                    icon={byPrefixAndName.fas["star"]}
                    className="text-yellow !h-[12px] !w-[12px] text-sm"
                  />
                ))}
              </span>
            </h2>
          </div>
          {/* Konum */}
          <p className="text-foreground text-sm">{hotelData.location}</p>
        </div>
        {/* Sağ taraf - Otel detayları */}
        <div className="flex flex-1 items-end justify-between gap-3">
          {/* Üst kısım - Otel bilgileri */}
          <div className="flex flex-1 flex-col gap-[10px] self-start xl:gap-4">
            {/* Otel adı ve yıldız rating */}
            <div className="hidden flex-col gap-[2px] lg:flex">
              <div className="">
                <h2 className="text-foreground flex flex-wrap items-center gap-x-[6px] text-lg font-semibold xl:gap-x-[10px]">
                  {hotelData.name}
                  <span className="flex items-center gap-[1px]">
                    {[...Array(hotelData.starRating)].map((_, index) => (
                      <FontAwesomeIcon
                        key={index}
                        icon={byPrefixAndName.fas["star"]}
                        className="text-yellow !h-[12px] !w-[12px]"
                      />
                    ))}
                  </span>
                </h2>
              </div>
              {/* Konum */}
              <p className="text-foreground text-sm">{hotelData.location}</p>
            </div>

            <div className="flex flex-col gap-[10px] xl:gap-[14px]">
              {/* Rating ve yorumlar */}
              {hotelData.rating !== null &&
                hotelData.rating !== undefined &&
                typeof hotelData.rating === "number" &&
                hotelData.rating > 0 &&
                hotelData.ratingText && (
                  <div className="flex items-center gap-[6px]">
                    <div className="bg-foreground w-[26px] rounded-sm px-[5px] py-[2px] text-center text-xs font-medium text-white">
                      {hotelData.rating}
                    </div>
                    <span className="text-foreground text-sm font-medium">
                      {hotelData.ratingText}
                    </span>
                    {/* <span className="text-sm text-foreground">•</span> */}
                    {/* <span className="text-foreground-muted text-sm">{hotelData.reviewCount} yorum</span> */}
                  </div>
                )}

              {/* Kahvaltı dahil bilgisi */}
              {hotelData.breakfast && (
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon
                    className="text-teal h-[18px]! w-[18px]!"
                    icon={byPrefixAndName.fak["custom-checkmark-fat-1"]}
                  />
                  <span className="text-teal text-sm">
                    {hotelData.breakfast}
                  </span>
                </div>
              )}

              {/* Otel olanakları */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {hotelData.amenities?.slice(0, 3).map((amenity, index) => {
                  const iconName = amenityIconMap[amenity.Code] || "home";
                  // Öncelik sırası: fak (custom) -> fas (FontAwesome solid) -> fallback home
                  const icon =
                    byPrefixAndName.fak[iconName] ||
                    byPrefixAndName.fal[iconName] ||
                    byPrefixAndName.fal["home"];
                  return (
                    <div key={index} className="flex items-center gap-[6px]">
                      <FontAwesomeIcon
                        icon={icon}
                        className="text-foreground h-[18px]! w-[18px]! text-sm"
                      />
                      <span className="text-foreground text-sm">
                        {amenity.Name || amenity}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Alt kısım - Fiyat ve seç butonu */}
          <div className="flex basis-[153px] flex-col items-end justify-between gap-4 xl:basis-[173px]">
            <button
              onClick={handleShowPrices}
              className="bg-primary h-10 w-full cursor-pointer rounded-lg px-4 text-sm font-medium text-white"
            >
              {t("showPrices")}
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
